import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { api } from "@/lib/api";
import { arDigits } from "@/lib/utils";
import { fmtAED, fmtMonth, N } from "./intel";
import RoundSelect from "./RoundSelect";

const ALL = "__all";
const LABELS = {
  under_1m: ["Under AED 1M", "أقل من مليون درهم"],
  "1m_2m": ["AED 1–2M", "١–٢ مليون درهم"],
  "2m_5m": ["AED 2–5M", "٢–٥ ملايين درهم"],
  "5m_10m": ["AED 5–10M", "٥–١٠ ملايين درهم"],
  over_10m: ["AED 10M+", "١٠ ملايين درهم فأكثر"],
};
const L = {
  en: { h: "Where Dubai's sales money goes", sub: (a, m) => `Registered sales by transaction price · ${a} · ${m}`, all: "All Dubai", area: "Area", sales: "sales", total: (n, v) => `Total: ${n} sales · ${v}`, note: "Each sale is counted once, in the band of its registered DLD value (lower bound included). Figures describe recorded activity only, not investment suitability.", na: "Price-band data is temporarily unavailable.", complete: (m) => `Shown for the latest complete month (${m}).` },
  ar: { h: "أين تذهب أموال المبيعات في دبي", sub: (a, m) => `المبيعات المسجّلة حسب سعر الصفقة · ${a} · ${m}`, all: "كل دبي", area: "المنطقة", sales: "صفقة", total: (n, v) => `الإجمالي: ${n} صفقة · ${v}`, note: "تُحتسب كل صفقة مرة واحدة ضمن شريحة قيمتها المسجّلة لدى الدائرة (الحد الأدنى مشمول). تصف الأرقام النشاط المسجّل فقط ولا تدلّ على ملاءمة الاستثمار.", na: "بيانات شرائح الأسعار غير متاحة مؤقتاً.", complete: (m) => `معروض لآخر شهر مكتمل (${m}).` },
};
const pc = (v, isAR) => (isAR ? `${arDigits(v.toFixed(1))}٪` : `${v.toFixed(1)}%`);

function Bars({ row, isAR, l }) {
  const maxShare = Math.max(...row.bands.map((b) => b.share_pct || 0), 1);
  return (
    <div className="flex-1 flex flex-col min-h-[260px]">
      <div className="flex-1 grid grid-cols-5 gap-2 sm:gap-4 items-end" aria-hidden="true">
        {row.bands.map((b, i) => (
          <div key={b.band} className="flex flex-col items-center justify-end h-full">
            <span className="text-[12px] sm:text-sm font-semibold tabular-nums mb-1.5 text-propertic-navy">{pc(b.share_pct || 0, isAR)}</span>
            <div className="w-full max-w-[72px] rounded-t-lg transition-[height] duration-500" style={{ height: `${Math.max(3, ((b.share_pct || 0) / maxShare) * 100)}%`, background: i === 0 ? "#0A2540" : `rgba(10,37,64,${0.82 - i * 0.14})` }} />
          </div>
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-5 gap-2 sm:gap-4 text-center border-t hairline pt-3">
        {row.bands.map((b) => (
          <li key={b.band} className="min-w-0" data-testid={`price-band-${b.band}`}>
            <p className="text-[11px] sm:text-[12px] font-semibold leading-tight text-propertic-ink">{LABELS[b.band][isAR ? 1 : 0]}</p>
            <p className="text-[10px] sm:text-[11px] text-propertic-muted mt-1 tabular-nums" data-testid={`price-band-${b.band}-count`}>{N(b.sales_count, isAR)} {l.sales}</p>
            <p className="text-[10px] sm:text-[11px] text-propertic-muted tabular-nums" data-testid={`price-band-${b.band}-value`}>{fmtAED(b.sales_aed, isAR)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PriceBands({ month, rows, isAR }) {
  const l = L[isAR ? "ar" : "en"];
  const [area, setArea] = useState(ALL);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/market/price-bands", { params: area !== ALL ? { market_area: area } : {} }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [area]);
  const m = data && (data.by_month[month] ? month : data.months[data.months.length - 1]);
  const row = data && data.by_month[m];
  const areaLabel = area !== ALL ? rows.find((r) => r.key === area)?.label || area : l.all;
  const options = [{ value: ALL, label: l.all }, ...[...rows].sort((a, b) => a.label.localeCompare(b.label)).map((r) => ({ value: r.key, label: r.label }))];
  return (
    <section id="intel-price-bands" className="h-full flex flex-col rounded-[1.5rem] border hairline bg-white/80 p-5 sm:p-6 scroll-mt-24 min-w-0" data-testid="price-bands">
      <h3 className="font-editorial text-xl sm:text-2xl text-propertic-navy">{l.h}</h3>
      <p className="text-[13px] text-propertic-muted mt-1 min-h-[20px]" data-testid="price-bands-month">{row ? l.sub(areaLabel, fmtMonth(m, isAR)) : ""}</p>
      <div className="mt-4 flex items-center gap-2 text-xs text-propertic-muted">
        <span className="shrink-0">{l.area}</span>
        <RoundSelect value={area} onChange={setArea} options={options} label={l.area} testId="price-bands-area-select" isAR={isAR} className="flex-1" />
      </div>
      {data === null && <p className="mt-4 text-sm text-propertic-muted" data-testid="price-bands-loading">…</p>}
      {data === false && <p className="mt-4 text-sm text-propertic-muted" data-testid="price-bands-unavailable">{l.na}</p>}
      {row && (
        <>
          {m !== month && <p className="mt-3 text-[11px] text-propertic-gold" data-testid="price-bands-complete-note">{l.complete(fmtMonth(m, isAR))}</p>}
          <div className="mt-6 flex-1 flex flex-col"><Bars row={row} isAR={isAR} l={l} /></div>
          <p className="mt-4 text-[12px] font-semibold text-propertic-navy" data-testid="price-bands-total">{l.total(N(row.total_sales_count, isAR), fmtAED(row.total_sales_aed, isAR))}</p>
          <p className="mt-3 flex gap-2 rounded-xl bg-propertic-sand/50 p-3 text-[11px] text-propertic-muted leading-relaxed"><Info className="w-4 h-4 shrink-0" />{l.note}</p>
        </>
      )}
    </section>
  );
}
